import React, { useState } from 'react';
import { Form, Button, Container, Alert } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { State } from '../../state';
import styles from './ContactForm.module.css';
import contactService from '../../services/contactService';

interface ContactFormData {
  companyName: string;
  fullName: string;
  email: string;
  phone: string;
  message: string;
}

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    companyName: '',
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const { data: languageData } = useSelector((state: State) => state.languageReducer);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      await contactService.submitContactForm(formData);
      setSubmitStatus({
        type: 'success',
        message: languageData?.contact?.successMessage
      });
      setFormData({
        companyName: '',
        fullName: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: languageData?.contact?.errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className={styles.contactFormContainer}>
      <h2 className={styles.formTitle}>{languageData?.contact?.title}</h2>
      <p className={styles.formSubtitle}>{languageData?.contact?.subtitle}</p>

      {submitStatus.type && (
        <Alert variant={submitStatus.type === 'success' ? 'success' : 'danger'}>
          {submitStatus.message}
        </Alert>
      )}

      <Form onSubmit={handleSubmit} className={styles.form}>
        <Form.Group className="mb-3">
          <Form.Label>{languageData?.contact?.companyName}</Form.Label>
          <Form.Control
            type="text"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{languageData?.contact?.fullName}</Form.Label>
          <Form.Control
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{languageData?.contact?.email}</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{languageData?.contact?.phone}</Form.Label>
          <Form.Control
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{languageData?.contact?.message}</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Button 
          type="submit" 
          variant="primary" 
          disabled={isSubmitting}
          className={styles.submitButton}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {languageData?.contact?.submitting}
            </>
          ) : (
            languageData?.contact?.submit
          )}
        </Button>
      </Form>
    </Container>
  );
};

export default ContactForm; 